import React, { useEffect, useState } from 'react';
import {
	Box,
	Menu,
	MenuItem,
	Typography,
	Button,
	ListItem,
	ListItemText,
	List,
	ListItemSecondaryAction,
	CircularProgress,
} from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { RiArrowDownSLine, RiCheckFill } from 'react-icons/ri';
import { upperFirst } from 'lodash';
import { changePrimaryPaymentCard } from 'common/api/db/functions';
import AddCardDialog from 'components/PaymentMethods/AddCardDialog';

import { useSelector } from 'react-redux';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { useTranslation } from 'services/localization';
import { getShopPrimaryPaymentMethod } from 'common/modules/billing';

const PaymentMethodSelect = () => {
	const classes = useStyles();
	const { t } = useTranslation();
	const theme = useTheme();

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const savedCardInformation = useSelector(ShopSelectors.shopPaymentCards);
	const adyenPaymentMethodsLoading = useSelector(ShopSelectors.adyenPaymentMethodsLoading);
	const adyenPaymentMethodsById = useSelector(ShopSelectors.paymentMethodsStoredInAdyenById);

	const [primaryCardToken, setPrimaryCardToken] = useState<string | undefined>(
		getShopPrimaryPaymentMethod(savedCardInformation)?.cardToken,
	);

	const getCardDetails = (cardToken: string | undefined) => {
		if (!cardToken) return undefined;
		return adyenPaymentMethodsById[cardToken];
	};

	const primaryCardDetails = getCardDetails(primaryCardToken);

	useEffect(() => {
		if (!!primaryCardToken) return;
		if (savedCardInformation.length > 0) {
			setPrimaryCardToken(getShopPrimaryPaymentMethod(savedCardInformation)?.cardToken);
		}
	}, [savedCardInformation, primaryCardToken]);

	const handlePaymentMethodSelect = async (cardToken: string | undefined) => {
		if (!cardToken) return;
		setPrimaryCardToken(cardToken);
		changePrimaryPaymentCard({
			shopId,
			cardToken,
		});
	};

	const renderLoading = () => {
		return (
			<Box height={100} width="100%" display="flex" justifyContent="center" alignItems="center">
				<CircularProgress />
			</Box>
		);
	};

	const renderPaymentMethods = () => {
		return (
			<Box>
				<List className={classes.list}>
					<ListItem
						button
						onClick={(e) => setAnchorEl(e.currentTarget)}
						className={classes.defaultPaymentMethod}
					>
						<ListItem
							className={classes.defaultPaymentMethod}
							classes={{
								container: classes.defaultPaymentMethodContainer,
							}}
						>
							<ListItemText
								primary={`${upperFirst(primaryCardDetails?.name)} ${t(
									'shop.billingView.paymentCards.endingIn',
									'ending in',
								)} ${primaryCardDetails?.lastFour}`}
								secondary={`${t('shop.billingView.paymentCards.expires', 'Expires')} ${
									primaryCardDetails?.expiryMonth
								}/${primaryCardDetails?.expiryYear}`}
							/>
							{savedCardInformation.length === 1 && (
								<RiCheckFill color={theme.palette.success.main} />
							)}
							{savedCardInformation.length > 1 && (
								<ListItemSecondaryAction>
									<RiArrowDownSLine />
								</ListItemSecondaryAction>
							)}
						</ListItem>
					</ListItem>
				</List>
				<Menu
					open={savedCardInformation.length > 1 && Boolean(anchorEl)}
					onClose={() => setAnchorEl(null)}
					onClick={() => setAnchorEl(null)}
					keepMounted
					anchorEl={anchorEl}
					classes={{
						paper: classes.menu,
					}}
				>
					{savedCardInformation.map((paymentMethod) => (
						<MenuItem key={paymentMethod.cardToken}>
							<ListItem onClick={() => handlePaymentMethodSelect(paymentMethod.cardToken)}>
								<ListItemText
									primary={`${upperFirst(getCardDetails(paymentMethod.cardToken)?.name)} ${t(
										'shop.billingView.paymentCards.endingIn',
										'ending in',
									)} ${getCardDetails(paymentMethod.cardToken)?.lastFour}`}
									secondary={`${t('shop.billingView.paymentCards.expires', 'Expires')} ${
										getCardDetails(paymentMethod.cardToken)?.expiryMonth
									}/${getCardDetails(paymentMethod.cardToken)?.expiryYear}`}
								/>
							</ListItem>
						</MenuItem>
					))}
				</Menu>
			</Box>
		);
	};

	const renderNoPaymentMethods = () => {
		return (
			<Box>
				<Typography variant="body2">
					{t('common:paymentMethods.noPaymentMethodsAdded', 'No payment methods added')}
				</Typography>
				<Box mt={2} />
				<AddCardDialog>
					{({ openDialog }) => (
						<Button variant="contained" color="primary" fullWidth onClick={openDialog}>
							{t('common:paymentMethods.addCard', 'Add card')}
						</Button>
					)}
				</AddCardDialog>
			</Box>
		);
	};

	return adyenPaymentMethodsLoading
		? renderLoading()
		: !!primaryCardDetails
		? renderPaymentMethods()
		: renderNoPaymentMethods();
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		list: {
			padding: 0,
		},
		defaultPaymentMethod: {
			width: '100%',
			padding: 0,
		},
		defaultPaymentMethodContainer: {
			width: '100%',
		},
		menu: {
			width: 300,
			maxWidth: '100%',
		},
	}),
);

export default PaymentMethodSelect;
